import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded"
import SwapVertIcon from "@mui/icons-material/SwapVert"
import {
	Button,
	Grid,
	IconButton,
	MenuItem,
	Popover,
	Select,
	Typography,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { ReactComponent as Asc } from "../../assets/img/asc.svg"
import { ReactComponent as Desc } from "../../assets/img/desc.svg"

const Sort = ({
	handleSearch,
	sortCriteria,
	setSortCriteria,
	sortingOptions,
	availableOptionsInitial,
}) => {
	const theme = useTheme()
	const { t } = useTranslation()
	const [anchorEl, setAnchorEl] = useState(null)
	const [availableOptions, setAvailableOptions] = useState(
		availableOptionsInitial
	)
	const [sortingOptionsState, setSortingOptionsState] = useState(sortingOptions)

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const open = Boolean(anchorEl)

	const addSortCriteria = () => {
		if (availableOptions.length > 0)
			setSortCriteria([...sortCriteria, { field: "", order: "asc" }])
	}

	const removeSortCriteria = (index: number) => {
		const newSortCriteria = sortCriteria.filter((_, i) => i !== index)
		setSortCriteria(newSortCriteria)
		updateAvailableOptions(newSortCriteria)
	}

	const updateAvailableOptions = (newSortCriteria) => {
		const selectedFields = newSortCriteria.map((criteria) => criteria.field)
		const newListOptions = sortingOptions.filter((option) => {
			return !selectedFields.includes(option)
		})
		setAvailableOptions(newListOptions)
	}

	const handleFieldChange = (index: number, newField: string) => {
		const newSortCriteria = [...sortCriteria]

		newSortCriteria[index].field = newField
		setSortCriteria(newSortCriteria)
		updateAvailableOptions(newSortCriteria)
	}

	const handleOrderChange = (index: number, newOrder: string) => {
		const newSortCriteria = [...sortCriteria]
		newSortCriteria[index].order = newOrder
		setSortCriteria(newSortCriteria)
	}

	return (
		<div>
			<Button variant="outlined" onClick={handleClick}>
				<SwapVertIcon />
				<Typography
					component="div"
					variant="subtitle2"
					style={{ textTransform: "capitalize" }}
				>
					{t("sort.label", { ns: "sort" })}
				</Typography>
				<Typography
					component="div"
					variant="caption"
					style={{ textTransform: "lowercase" }}
				>
					({sortCriteria.length}{" "}
					{t("sort.field", {
						postProcess: "interval",
						count: sortCriteria.length,
						ns: "sort",
					})}
					)
				</Typography>
			</Button>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<div>
					{sortCriteria.map(
						(criteria: { field: string; order: string }, index: number) => (
							<Grid
								container
								sx={{ padding: "16px" }}
								gap="16px"
								alignItems={"center"}
								justifyContent={"space-between"}
								key={"criteria_" + index}
								style={{ minWidth: "355px", padding: "16px 8px" }}
							>
								<Grid item>
									<IconButton
										aria-label={t("sort.criteria.accessibility.remove", {
											ns: "sort",
										})}
										onClick={() => removeSortCriteria(index)}
									>
										<RemoveCircleOutlineRoundedIcon />
									</IconButton>
								</Grid>
								<Grid item>
									<Select
										size="small"
										value={criteria.field}
										onChange={(e) => {
											if (e.target.value)
												handleFieldChange(index, e.target.value)
										}}
										displayEmpty
									>
										<MenuItem value="" disabled>
											{t("sort.criteria.select", { ns: "sort" })}
										</MenuItem>
										{sortingOptionsState.map((option) => (
											<MenuItem
												key={option}
												value={option}
												disabled={!availableOptions.includes(option)}
											>
												{t(`sort.criteria.options.${option}`, { ns: "sort" })}
											</MenuItem>
										))}
									</Select>
								</Grid>
								<Grid item>
									<Grid container>
										<Button
											variant={
												criteria.order === "asc" ? "contained" : "outlined"
											}
											sx={{
												color:
													criteria.order === "asc"
														? theme.palette.primary.contrastText
														: theme.palette.secondary.contrastText,
												backgroundColor:
													criteria.order === "asc"
														? theme.palette.primary.main
														: theme.palette.secondary.main,
											}}
											onClick={(e) => handleOrderChange(index, "asc")}
											startIcon={
												<Asc
													color={
														criteria.order === "asc"
															? theme.palette.primary.contrastText
															: theme.palette.secondary.contrastText
													}
													style={{
														backgroundColor:
															criteria.order === "asc"
																? theme.palette.primary.main
																: theme.palette.secondary.main,
													}}
												/>
											}
										>
											ASC
										</Button>
									</Grid>
								</Grid>
								<Grid item>
									<Button
										variant={
											criteria.order === "desc" ? "contained" : "outlined"
										}
										sx={{
											color:
												criteria.order === "desc"
													? theme.palette.primary.contrastText
													: theme.palette.secondary.contrastText,
											backgroundColor:
												criteria.order === "desc"
													? theme.palette.primary.main
													: theme.palette.secondary.main,
										}}
										onClick={(e) => handleOrderChange(index, "desc")}
										startIcon={
											<Desc
												color={
													criteria.order === "desc"
														? theme.palette.primary.contrastText
														: theme.palette.secondary.contrastText
												}
												style={{
													backgroundColor:
														criteria.order === "desc"
															? theme.palette.primary.main
															: theme.palette.secondary.main,
												}}
											/>
										}
									>
										DESC
									</Button>
								</Grid>
							</Grid>
						)
					)}
					<Grid
						justifyContent={"space-between"}
						alignItems={"center"}
						sx={{ backgroundColor: "#F1F1F1" }}
					>
						{availableOptions.length > 0 && (
							<Button
								sx={{
									width: "calc(50% - 14px)",
									marginLeft: "16px",
									backgroundColor: "#FFF",
								}}
								variant="outlined"
								onClick={addSortCriteria}
								startIcon={<AddCircleOutlineRoundedIcon />}
							>
								{t("sort.criteria.add", { ns: "sort" })}
							</Button>
						)}
						<Button
							variant="contained"
							onClick={handleSearch}
							style={{ margin: "16px" }}
						>
							{t("sort.criteria.send", { ns: "sort" })}
						</Button>
					</Grid>
				</div>
			</Popover>
		</div>
	)
}

export default Sort
